<template>
  <div class="prst">
    <!-- Filters bar: chips + search -->
    <div class="prst-filters">
      <div class="prst-filters__chips">
        <button
          v-for="chip in filterChips"
          :key="chip.key"
          type="button"
          class="prst-chip"
          @click="$emit('remove-filter', chip.key)"
        >
          <span>{{ chip.label }}</span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M15 5L5 15M5 5l10 10"
              stroke="#344054"
              stroke-width="1.67"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span class="prst-visually-hidden">Remove {{ chip.label }} filter</span>
        </button>

        <button type="button" class="prst-chip prst-chip--more" @click="$emit('open-more-filters')">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M2.5 5h15M5.833 10h8.334M8.333 15h3.334"
              stroke="#344054"
              stroke-width="1.67"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span>More filters</span>
        </button>
      </div>

      <div class="prst-search">
        <ExTextInput
          :placeholder="searchPlaceholder"
          icon="interface_search"
          clearText="Clear search"
          :value="searchValue"
          @input="handleSearchInput"
        />
      </div>
    </div>

    <!-- As-of row -->
    <div class="prst-asof-row">
      <div class="prst-asof-row__spacer" />
      <div class="prst-asof">
        <AsOfDateNavigator
          :today="asOfState === AS_OF_STATE.TODAY"
          :as-of-date="asOfDate"
          @reset-to-today="$emit('reset-to-today')"
          @open-date-picker="openDatePicker"
        />

        <DatePicker
          v-if="isDatePickerOpen"
          :isDatePickerOpen="isDatePickerOpen"
          :value="asOfDate"
          :modal="true"
          @onApplyDate="handleApplyDate"
          @onClose="closeDatePicker"
        />
      </div>
    </div>

    <!-- Table -->
    <div class="prst-table-wrap">
      <div
        v-if="loading"
        class="prst-loading"
        role="status"
        aria-live="polite"
        data-testid="positions-table-skeleton"
      >
        <div v-for="n in 5" :key="`prst-skeleton-${n}`" class="prst-skeleton-row">
          <span v-for="cell in 5" :key="`prst-skeleton-${n}-${cell}`" class="prst-skeleton-cell" />
        </div>
      </div>

      <div v-else-if="error" class="prst-error" role="alert">
        <EXErrorState
          title="Failed to load positions"
          :message="errorText"
          @retry="$emit('retry')"
        />
      </div>

      <table v-if="!loading && !error" class="prst-table">
        <colgroup>
          <col class="prst-col-position" />
          <col class="prst-col-department" />
          <col class="prst-col-occupant" />
          <col class="prst-col-subscription" />
          <col class="prst-col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Position</th>
            <th scope="col">Department</th>
            <th scope="col">Occupied by</th>
            <th scope="col">
              <div class="prst-sub-header">
                <span class="prst-sub-header__label">Pay Range Subscription</span>
                <span v-if="readinessEnabled" class="prst-legend">0%-50%-100%-150%</span>
              </div>
            </th>
            <th scope="col" aria-hidden="true"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in displayPositions" :key="row.id">
            <td>
              <button
                type="button"
                class="prst-position"
                :aria-label="`Open Fair Pay for ${row.name}`"
                @click="$emit('open-position', row)"
              >
                <span class="prst-position__code">{{ row.code }}</span>
                <span class="prst-position__name">{{ row.name }}</span>
              </button>
            </td>
            <td
              class="prst-department"
              :class="row.subscription ? 'prst-department--active' : 'prst-department--muted'"
            >
              {{ row.department }}
            </td>
            <td>
              <div v-if="row.occupant" class="prst-employee-cell">
                <EmployeeCard
                  :first-name="row.occupant.firstName"
                  :last-name="row.occupant.lastName"
                  :photo-url="row.occupant.avatarUrl"
                  :availability="row.occupant.availability"
                  :is-allocated="Boolean(row.occupant.isAllocated)"
                  :hide-availability-selector="true"
                  :show-availability="true"
                  :show-importance-selector="false"
                  :is-hover-enabled="false"
                  :show-link-underline="false"
                  text-column-max-width="180px"
                />
              </div>
              <div v-else class="prst-vacant">Vacant</div>
            </td>
            <td>
              <div class="prst-sub-cell">
                <button
                  v-if="
                    subscriptionNavigationEnabled &&
                    subscriptionChevronShown(row.previousSubscriptionEffectiveTo)
                  "
                  type="button"
                  class="prst-sub-nav prst-sub-nav--previous"
                  :aria-label="`Previous subscription for ${row.name}`"
                  @click="onPreviousSubscription(row)"
                >
                  <svg
                    class="prst-sub-nav__icon"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M15 18L9 12L15 6"
                      stroke="#667085"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
                <span
                  v-else-if="row.subscription || subscriptionNavigationEnabled"
                  class="prst-sub-nav-placeholder"
                  aria-hidden="true"
                ></span>
                <template v-if="row.subscription">
                  <div class="prst-sub-cell__card">
                    <PayRangeSubscriptionCard
                      v-bind="subscriptionCardProps(row.subscription)"
                      @unsubscribe="$emit('unsubscribe-position', row)"
                      @open-pay-range="$emit('open-pay-range', $event)"
                      @navigate-to-previous-version="$emit('navigate-to-previous-version', $event)"
                      @navigate-to-next-version="$emit('navigate-to-next-version', $event)"
                    />
                  </div>
                  <CoverageGapBadge
                    v-if="coverageGapEnabled"
                    :coverage-gaps="row.subscription.coverageGaps"
                    :today="asOfDate"
                  />
                </template>
                <button
                  v-else
                  type="button"
                  class="prst-subscribe-btn"
                  @click="$emit('subscribe-position', row)"
                >
                  + Subscribe to Pay Range
                </button>
                <button
                  v-if="
                    subscriptionNavigationEnabled &&
                    subscriptionChevronShown(row.nextSubscriptionEffectiveFrom)
                  "
                  type="button"
                  class="prst-sub-nav prst-sub-nav--next"
                  :aria-label="`Next subscription for ${row.name}`"
                  @click="onNextSubscription(row)"
                >
                  <svg
                    class="prst-sub-nav__icon"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M9 18L15 12L9 6"
                      stroke="#667085"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </td>
            <td>
              <div class="prst-actions">
                <ChatterIcon
                  :aria-label="`Open chatter for ${row.name}`"
                  @onClick="$emit('open-position-chatter', row)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loading && !error && !positions.length" class="prst-empty">
        <EXEmptyState
          :title="emptyTitle"
          :supporting-text="emptyMessage"
          title-class="prst-empty__title"
          supporting-text-class="prst-empty__subtitle"
        />
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="pageCount > 1 && !loading && !error" class="prst-pagination">
      <button type="button" class="prst-page-btn" :disabled="page <= 1" @click="goToPage(page - 1)">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="M15.833 10H4.167M4.167 10 10 15.833M4.167 10 10 4.167"
            stroke="#344054"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        Previous
      </button>

      <div class="prst-page-numbers">
        <span v-for="item in pageItems" :key="item.key">
          <span v-if="item.ellipsis" class="prst-page-num prst-page-num--ellipsis">...</span>
          <button
            v-else
            type="button"
            class="prst-page-num"
            :class="{ 'prst-page-num--active': item.page === page }"
            :aria-current="item.page === page ? 'page' : null"
            :aria-label="`Page ${item.page}`"
            @click="goToPage(item.page)"
          >
            {{ item.page }}
          </button>
        </span>
      </div>

      <button
        type="button"
        class="prst-page-btn"
        :disabled="page >= pageCount"
        @click="goToPage(page + 1)"
      >
        Next
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="M4.167 10h11.666M15.833 10 10 4.167M15.833 10 10 15.833"
            stroke="#344054"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
export const AS_OF_STATE = {
  CUSTOM_DATE: "CUSTOM_DATE",
  TODAY: "TODAY",
};
</script>

<script setup>
import { computed, ref } from "vue";
import dayjs from "dayjs";
import EmployeeCard from "@components/organizations/employees/EmployeeCard/EmployeeCard.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import EXEmptyState from "@components/EXEmptyState/EXEmptyState.vue";
import EXErrorState from "@components/EXErrorState/EXErrorState.vue";
import DatePicker from "@components/DatePicker/DatePicker.vue";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import PayRangeSubscriptionCard from "@components/PayRangeSubscriptionCard/PayRangeSubscriptionCard.vue";
import CoverageGapBadge from "@components/CoverageGapBadge/CoverageGapBadge.vue";
import AsOfDateNavigator from "@components/AsOfDateNavigator/AsOfDateNavigator.vue";
import { useReadinessEnabled } from "@components/utils/useReadinessEnabled";
import { resolveSubscriptionForAsOf, resolveCardVersion } from "./subscriptionResolver.js";

const props = defineProps({
  /** Rows for the table. Each row: { id, code, name, department, occupant, subscription, previousSubscriptionEffectiveTo, nextSubscriptionEffectiveFrom }. */
  positions: {
    type: Array,
    default: () => [],
  },
  /** ISO date used to resolve each position's current subscription (Rule 10). Owned by the consuming page. */
  asOfDate: {
    type: String,
    default: () => dayjs().format("YYYY-MM-DD"),
  },
  /** Stable enum key mirroring the Figma AsOf variant property. */
  asOfState: {
    type: String,
    default: AS_OF_STATE.TODAY,
    validator: (v) => Object.values(AS_OF_STATE).includes(v),
  },
  filterChips: {
    type: Array,
    default: () => [],
  },
  searchPlaceholder: {
    type: String,
    default: "Search Position",
  },
  page: {
    type: Number,
    default: 1,
  },
  pageCount: {
    type: Number,
    default: 1,
  },
  /** Rule 5 — enable version-navigation chevrons on subscription cards (flag-gated by the page). */
  versionNavigationEnabled: {
    type: Boolean,
    default: false,
  },
  /** Rule 4 — enable subscription-navigation chevrons around each row's card (flag-gated by the page). */
  subscriptionNavigationEnabled: {
    type: Boolean,
    default: false,
  },
  /** ER-11714 — enable the coverage-gap pill beside each row's subscription card (flag-gated by the page). */
  coverageGapEnabled: {
    type: Boolean,
    default: false,
  },
  /** ER-12850 — table-level states owned by the consuming page's fetch. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** True/string message shown in place of the table when the fetch fails. */
  error: {
    type: [Boolean, String],
    default: false,
  },
  emptyTitle: {
    type: String,
    default: "No positions with pay range subscriptions",
  },
  emptyMessage: {
    type: String,
    default: "Subscribe a position to a pay range to see it here.",
  },
});

const emit = defineEmits([
  "update:page",
  "update:search",
  "update:asOfDate",
  "reset-to-today",
  "remove-filter",
  "open-more-filters",
  "subscribe-position",
  "unsubscribe-position",
  "open-position",
  "open-position-chatter",
  "open-pay-range",
  "navigate-to-previous-version",
  "navigate-to-next-version",
  "navigate-to-previous-subscription",
  "navigate-to-next-subscription",
  "retry",
]);

const searchValue = ref("");
const isDatePickerOpen = ref(false);

const errorText = computed(() =>
  typeof props.error === "string" && props.error
    ? props.error
    : "Something went wrong while loading positions. Please try again."
);

function handleSearchInput(value) {
  searchValue.value = value;
  emit("update:search", value);
}

const displayPositions = computed(() =>
  props.positions.map((row) => ({
    ...row,
    subscription: resolveSubscriptionForAsOf(row.subscription, props.asOfDate),
  }))
);

// The positions payload carries no `readiness_enabled`, so this falls back to the account product flag.
const readinessEnabled = useReadinessEnabled(null);

function subscriptionCardProps(subscription) {
  const version = resolveCardVersion(subscription, props.asOfDate);
  return {
    readinessEnabled: readinessEnabled.value,
    subscriptionId: subscription.id,
    payRangeId: subscription.payRangeId,
    payRangeCode: subscription.payRangeCode,
    payRangeName: subscription.payRangeName,
    status: subscription.status,
    countryCode: subscription.countryCode,
    countryFlag: subscription.countryFlag,
    currencyCode: subscription.currencyCode,
    payFrequency: subscription.payFrequency,
    locationName: subscription.locationName,
    subscriptionEffectiveFrom: subscription.effectiveFrom,
    subscriptionEffectiveTo: subscription.effectiveTo,
    versionEffectiveFrom: version.versionEffectiveFrom,
    versionEffectiveTo: version.versionEffectiveTo,
    versionNumber: version.versionNumber,
    ...(props.versionNavigationEnabled
      ? {
          previousVersionEffectiveTo: subscription.previousVersionEffectiveTo ?? null,
          nextVersionEffectiveFrom: subscription.nextVersionEffectiveFrom ?? null,
        }
      : {}),
    amountMin: subscription.readiness?.min,
    amountAt50: subscription.readiness?.at50,
    amountAt100: subscription.readiness?.at100,
    amountMax: subscription.readiness?.max,
    today: props.asOfDate,
  };
}

/** Rule 4 — a subscription-navigation chevron renders only when the adjacent date exists. */
function subscriptionChevronShown(adjacentDate) {
  return adjacentDate != null && adjacentDate !== "";
}

/** Rule 4 — left chevron: step the as-of date to the previous subscription's own effective-by date. */
function onPreviousSubscription(row) {
  const targetDate = row.previousSubscriptionEffectiveTo;
  if (!subscriptionChevronShown(targetDate)) return;
  emit("navigate-to-previous-subscription", { positionId: row.id, targetDate });
}

/** Rule 4 — right chevron: step the as-of date to the next subscription's own effective-on date. */
function onNextSubscription(row) {
  const targetDate = row.nextSubscriptionEffectiveFrom;
  if (!subscriptionChevronShown(targetDate)) return;
  emit("navigate-to-next-subscription", { positionId: row.id, targetDate });
}

function buildPageItems(current, total) {
  if (total <= 0) return [];
  const keep = new Set(
    [1, 2, 3, total - 2, total - 1, total, current - 1, current, current + 1].filter(
      (p) => p >= 1 && p <= total
    )
  );
  const sorted = [...keep].sort((a, b) => a - b);
  const items = [];
  let previous = 0;
  sorted.forEach((p) => {
    if (p - previous > 1) {
      items.push({ key: `ellipsis-${p}`, ellipsis: true });
    }
    items.push({ key: `page-${p}`, page: p });
    previous = p;
  });
  return items;
}

const pageItems = computed(() => buildPageItems(props.page, props.pageCount));

function goToPage(next) {
  if (next < 1 || next > props.pageCount || next === props.page) return;
  emit("update:page", next);
}

function openDatePicker() {
  isDatePickerOpen.value = true;
}

function closeDatePicker() {
  isDatePickerOpen.value = false;
}

function formatLocalDateToISO(dateStr) {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function handleApplyDate(date) {
  if (date) {
    emit("update:asOfDate", formatLocalDateToISO(date));
  }
  closeDatePicker();
}
</script>

<style scoped>
.prst {
  --prst-sub-card-width: 365px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06);
  overflow: hidden;
  font-family: Inter, sans-serif;
}

.prst-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ── Filters bar ───────────────────────────── */
.prst-filters {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
}

.prst-filters__chips {
  display: flex;
  flex: 1 0 0;
  gap: 12px;
  align-items: center;
  min-width: 0;
}

.prst-chip {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
  cursor: pointer;
  white-space: nowrap;
}

.prst-chip--more {
  font-weight: 600;
}

.prst-search {
  display: flex;
  flex: 1 0 0;
  align-items: center;
  max-width: 320px;
}

/* ── As-of row ─────────────────────────────── */
.prst-asof-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 5px 20px;
}

.prst-asof-row__spacer {
  flex: 1 0 0;
  height: 40px;
}

.prst-asof {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* ── Table ─────────────────────────────────── */
.prst-table-wrap {
  width: 100%;
  overflow-x: auto;
}

.prst-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.prst-col-position {
  width: 220px;
}

.prst-col-department {
  width: 140px;
}

.prst-col-occupant {
  width: 242px;
}

.prst-col-actions {
  width: 120px;
}

.prst-table th {
  background: #f9fafb;
  border-bottom: 1px solid #eaecf0;
  padding: 12px 24px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #667085;
  text-align: left;
  white-space: nowrap;
}

.prst-col-subscription {
  min-width: 420px;
}

.prst-sub-header {
  display: flex;
  align-items: center;
  gap: 4px;
}

.prst-sub-header__label {
  width: var(--prst-sub-card-width);
  flex-shrink: 0;
}

.prst-legend {
  flex: 1 1 0;
  font-weight: 500;
  text-align: center;
}

.prst-table td {
  border-bottom: 1px solid #eaecf0;
  padding: 16px 24px;
  vertical-align: middle;
  font-size: 14px;
  color: #344054;
}

.prst-position {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.prst-position__code {
  font-weight: 700;
  color: #344054;
}

.prst-position__name {
  font-weight: 400;
  color: #344054;
}

.prst-table td.prst-department--active {
  color: #344054;
}

.prst-table td.prst-department--muted {
  color: #98a2b3;
}

.prst-employee-cell {
  display: flex;
  align-items: center;
  min-width: 0;
}

/* Left edge matches EmployeeCard's name column: 40px avatar + 20px + 20px margins + 6px padding. */
.prst-vacant {
  padding-left: 86px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: var(--colors-text-text-tertiary-600, #475467);
}

.prst-subscribe-btn {
  display: inline-flex;
  align-items: center;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #7f56d9;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #7f56d9;
  cursor: pointer;
  white-space: nowrap;
}

.prst-subscribe-btn:hover {
  background: #f9f5ff;
}

/* ── Rule 4 — subscription-navigation chevrons ── */
.prst-sub-cell {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.prst-sub-cell__card {
  flex: 0 0 var(--prst-sub-card-width);
  width: var(--prst-sub-card-width);
  min-width: 0;
}

.prst-sub-nav-placeholder {
  display: block;
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
}

.prst-sub-nav {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 2px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  box-sizing: border-box;
}

.prst-sub-nav:hover {
  background: #f2f4f7;
}

.prst-sub-nav:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.prst-sub-nav__icon {
  display: block;
  width: 16px;
  height: 16px;
}

.prst-actions {
  display: flex;
  align-items: center;
  gap: 9px;
}

.prst-empty {
  border-top: 1px solid #eaecf0;
}

.prst-loading {
  border-top: 1px solid #eaecf0;
}

.prst-skeleton-row {
  display: grid;
  grid-template-columns: 220px 140px 242px minmax(280px, 1fr) 120px;
  gap: 24px;
  padding: 16px 24px;
  border-bottom: 1px solid #eaecf0;
}

.prst-skeleton-cell {
  height: 16px;
  border-radius: 6px;
  background: #f2f4f7;
  animation: prst-pulse 1.4s ease-in-out infinite;
}

.prst-error {
  border-top: 1px solid #eaecf0;
}

@keyframes prst-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}

/* ── Pagination ────────────────────────────── */
.prst-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px 16px;
}

.prst-page-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  font-size: 14px;
  font-weight: 500;
  color: #344054;
  cursor: pointer;
}

.prst-page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.prst-page-numbers {
  display: flex;
  gap: 2px;
}

.prst-page-num {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: #667085;
  cursor: pointer;
}

.prst-page-num--active {
  background: #f9f5ff;
  color: #7f56d9;
  cursor: default;
}

.prst-page-num--ellipsis {
  cursor: default;
  color: #667085;
}
</style>
